import { useCallback, useMemo } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import type { BillingPlanSummary, PlanTier } from "../types/billing";

interface ApiResponse<T> {
  status: string;
  data: T;
}

interface BillingPlanSummaryDto {
  plan_tier: PlanTier;
  beta_enabled?: boolean;
  beta_label?: string | null;
  subscription_status?: string | null;
  limits: {
    brands: number | null;
    monthly_generations: number | null;
    knowledge_sources: number | null;
  };
  usage: {
    brands: number;
    monthly_generations: number;
    knowledge_sources_for_active_brand?: number;
  };
  features: {
    content_history: boolean;
    scheduling: boolean;
    seo: boolean;
    social_signal_listening: boolean;
    leads: boolean;
    product_recommendations: boolean;
  };
  billing?: {
    cancel_at?: string | null;
    current_period_end?: string | null;
  };
}

export const usePlanApi = () => {
  const { get } = useHttpAdapter();

  const getCurrentPlan = useCallback(
    async (brandId?: string | null): Promise<BillingPlanSummary | null> => {
      try {
        const query = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : "";
        const response = await get<ApiResponse<BillingPlanSummaryDto>>(
          `/billing/plan${query}`
        );

        if (response.data?.status !== "success" || !response.data?.data) {
          return null;
        }

        const payload = response.data.data;
        return {
          planTier: payload.plan_tier,
          betaEnabled: payload.beta_enabled ?? false,
          betaLabel: payload.beta_label ?? null,
          subscriptionStatus: payload.subscription_status ?? null,
          limits: {
            brands: payload.limits.brands,
            monthlyGenerations: payload.limits.monthly_generations ?? null,
            knowledgeSources: payload.limits.knowledge_sources ?? null,
          },
          usage: {
            brands: payload.usage.brands,
            monthlyGenerations: payload.usage.monthly_generations,
            knowledgeSourcesForActiveBrand:
              payload.usage.knowledge_sources_for_active_brand ?? 0,
          },
          features: {
            contentHistory: payload.features.content_history,
            scheduling: payload.features.scheduling,
            seo: payload.features.seo,
            socialSignalListening: payload.features.social_signal_listening,
            leads: payload.features.leads,
            productRecommendations: payload.features.product_recommendations,
          },
          billing: {
            cancelAt: payload.billing?.cancel_at ?? null,
            currentPeriodEnd: payload.billing?.current_period_end ?? null,
          },
        } satisfies BillingPlanSummary;
      } catch (error) {
        console.error("Failed to fetch billing plan", error);
        return null;
      }
    },
    [get]
  );

  return useMemo(
    () => ({
      getCurrentPlan,
    }),
    [getCurrentPlan]
  );
};

export default usePlanApi;
